import React, { useEffect, useState } from "react";

import { ElementState, Preview } from "@creatomate/preview";
import { SelectValue, SelectValueWithCategory } from "@culturehq/components/dist/components/form/typings";

import { ElementSource } from "chq";
import { Trans } from "app/utils";
import { applyModificationsNoUndo } from "components/stories/video-studio/utils";
import { DYNAMIC_ELEMENT_MEDIA_OPTIONS, DYNAMIC_ELEMENT_ORG_FIELDS_OPTIONS, DYNAMIC_ELEMENT_TEXT_OPTIONS, DYNAMIC_WITH_FULL_NAME_OPTIONS, DYNAMIC_WITH_NAME_OPTIONS, RELATIVE_TO_DEFAULT_OPTIONS } from "components/stories/video-studio/utils/constants";
import { useTemplateBuilder } from "components/admin/templates/context/TemplateBuilderContext";
import { useProfile } from "app/context";
import TextProperty from "../TextProperty";

interface DynamicElementProps {
  activeElement: ElementState;
  preview: Preview;
  useDefaultValue: boolean;
}

const DynamicElement: React.FC<DynamicElementProps> = ({
  activeElement, preview, useDefaultValue
}) => {
  const {
    templateBuilderSelection,
    dynamicElements,
    setDynamicElements,
    relativeElements,
    setRelativeElements
  } = useTemplateBuilder();
  const useRelativeTo = templateBuilderSelection === "montage" || templateBuilderSelection === "video";
  const [dynamicSelection, setDynamicSelection] = useState<null | SelectValue>("static_element");
  const [relativeSelection, setRelativeSelection] = useState<null | SelectValue>("none");
  const [relativeToOptions, setRelativeToOptions] = useState(RELATIVE_TO_DEFAULT_OPTIONS);
  const source = preview.getSource();
  const { profile: { organization } } = useProfile();

  useEffect(() => {
    const otherDynamicElements = dynamicElements.filter(
      de => de.elementId !== activeElement.source.id).sort((a, b) => a.type.localeCompare(b.type));
    const elementsForRelative = otherDynamicElements.map(
      efr => {
        const option = DYNAMIC_ELEMENT_MEDIA_OPTIONS.find(option => option.value === efr.type);
        if (option) {
          let finalLabel = option.label;
          const index = getIndexByType(option.value, efr.elementId);

          if (index.use) {
            finalLabel = `${option.label} ${index.value}`;
          }

          return { label: finalLabel, value: efr.elementId };
        }

        return ({ label: "", value: "" });
      }
    ).filter(efr => efr.value !== "");
    if (elementsForRelative) {
      setRelativeToOptions(
        [
          ...RELATIVE_TO_DEFAULT_OPTIONS,
          ...elementsForRelative
        ]
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicElements, activeElement]);

  useEffect(() => {
    source.elements.forEach((element: ElementSource) => {
      if (element.name?.includes("I=")) {
        const value = element.name.split("@@").find((part: string) => part.startsWith("I="));
        if (!value) return;

        setDynamicElements(prev => {
          const currentDynamicElement = prev.find(
            de => de.elementId === element.id);
          if (currentDynamicElement) {
            return prev.map(de => (de.elementId === element.id ? {
              elementId: de.elementId,
              type: value
            } : de));
          }
          return [
            ...prev,
            {
              elementId: element.id,
              type: value
            }
          ];
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentRelativeElement = relativeElements.find(
      re => re.elementId === activeElement.source.id);

    const currentDynamicElement = dynamicElements.find(
      de => de.elementId === activeElement.source.id);

    if (currentDynamicElement) {
      const option = getDynamicOptions().find(
        option => option.value === currentDynamicElement.type
      );
      setDynamicSelection(option?.value || "static_element");
    } else {
      setDynamicSelection("static_element");
    }

    if (currentRelativeElement) {
      const type = dynamicElements.find(
        de => de.elementId === currentRelativeElement.relativeToId);
      if (type) {
        setRelativeSelection(type.elementId || "none");
      } else {
        setRelativeSelection(currentRelativeElement.relativeToId);
      }
    } else {
      setRelativeSelection("none");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relativeElements, activeElement]);


  const getIdentifierName = (newName: string, currentName: string) => {
    if (!currentName) return newName;
    const isStaticElement = newName === "static_element";
    const hasIdentifierId = currentName.includes("I=");
    const hasRelativeTo = currentName.includes("RT=");

    if (isStaticElement) return currentName.replace(/I=[^@]+/, "").replace("@@", "");
    if (hasRelativeTo && !hasIdentifierId) return `${currentName}@@${newName}`;
    if (hasIdentifierId) return currentName.replace(/I=[^@]+/, newName);

    return newName;
  };

  const getRelativeToName = (newName: string, currentName: string) => {
    if (!currentName) return newName;
    const isNoneValue = newName === "RT=none";
    const hasIdentifierId = currentName.includes("I=");
    const hasRelativeTo = currentName.includes("RT=");

    if (isNoneValue) return currentName.replace(/RT=[^@]+/, "").replace("RT", "").replace("@@", "");
    if (hasIdentifierId && !hasRelativeTo) return `${currentName}@@${newName}`;
    if (hasRelativeTo) return currentName.replace(/RT=[^@]+/, newName);

    return newName;
  };

  const applyModifications = async (value: string) => {
    const name = getIdentifierName(value, activeElement.source.name);
    await applyModificationsNoUndo({
      elements: { [activeElement.source.id]: { name } }
    }, preview);
  };

  const applyNameModification = async (value: string) => {
    const name = getRelativeToName(`RT=${value}`, activeElement.source.name);
    await applyModificationsNoUndo({
      elements: { [activeElement.source.id]: { name } }
    }, preview);
  };

  const handleDynamicElementChange = (
    value: null | SelectValue | SelectValueWithCategory) => {
    if (typeof value === "string") {
      setDynamicSelection(value);
      const currentDynamicElement = dynamicElements.find(
        de => de.elementId === activeElement.source.id);

      if (value !== "static_element") {
        setDynamicElements(prev => {
          if (currentDynamicElement) {
            return prev.map(de => (de.elementId === activeElement.source.id ? {
              elementId: de.elementId,
              type: value
            } : de));
          }
          return [
            ...prev,
            {
              elementId: activeElement.source.id,
              type: value
            }
          ];
        });
      } else if (currentDynamicElement) {
        setDynamicElements(prev => prev.filter(de => de.elementId !== activeElement.source.id));
        setRelativeElements(
          prev => prev.filter(de => de.relativeToId !== activeElement.source.id));
      }
      applyModifications(value);
    }
  };

  const handleRelativeToChange = (value: null | SelectValue | SelectValueWithCategory) => {
    if (typeof value === "string") {
      setRelativeSelection(value);
      const exist = relativeElements.findIndex(re => re.elementId === activeElement.source.id);
      const relativeToElement = dynamicElements.find(de => de.type === value);

      if (value !== "none") {
        setRelativeElements(prev => {
          if (exist > -1) {
            return prev.map(re => (re.elementId === activeElement.source.id ? {
              elementId: re.elementId,
              relativeToId: relativeToElement?.elementId || value
            } : re));
          }
          return [
            ...prev,
            {
              elementId: activeElement.source.id,
              relativeToId: relativeToElement?.elementId || value
            }
          ];
        });
      } else if (exist > -1) {
        setRelativeElements(prev => prev.filter(de => de.elementId !== activeElement.source.id));
      }
      applyNameModification(value);
    }
  };

  const getDynamicOptions = () => {
    if (activeElement.source.type === "text") {
      const nameOptions = organization.firstNameLastNameEnabled
        ? DYNAMIC_WITH_FULL_NAME_OPTIONS : DYNAMIC_WITH_NAME_OPTIONS;
      const companyField = organization.organizationFields?.find(field => field.table === "companies");

      let orgFieldsOptions = DYNAMIC_ELEMENT_ORG_FIELDS_OPTIONS;

      const findFieldLabel = (table: string) => organization
        .organizationFields?.find(field => field.table === table)?.label;

      if (!companyField) {
        orgFieldsOptions = orgFieldsOptions.filter(field => field.value !== "I=Creator_Company");
      }

      const departmentLabel = findFieldLabel("departments");
      const companyLabel = findFieldLabel("companies");
      orgFieldsOptions = orgFieldsOptions.map(option => {
        switch (option.value) {
          case "I=Creator_Deparment":
            return {
              label: departmentLabel ? `Creator ${departmentLabel}` : option.label,
              value: option.value
            };
          case "I=Creator_Company":
            return {
              label: companyLabel ? `Creator ${companyLabel}` : option.label,
              value: option.value
            };
          default:
            return option;
        }
      });

      return [
        ...DYNAMIC_ELEMENT_TEXT_OPTIONS,
        ...nameOptions,
        ...orgFieldsOptions
      ];
    }
    const activeElementIsImage = activeElement.source.type === "image";
    return activeElementIsImage ? DYNAMIC_ELEMENT_MEDIA_OPTIONS : DYNAMIC_ELEMENT_MEDIA_OPTIONS.filter(option => option.value !== "I=Creator_Profile_Image");
  };

  const getIndexByType = (type: string, elementId: string) => {
    const filteredElements = dynamicElements.filter(
      de => de.type === type
    );
    const elementIndex = filteredElements.findIndex(de => de.elementId === elementId);

    return { value: elementIndex + 1, use: filteredElements.length > 1 };
  };

  return (
    <div className="chq-ffd-color">
      <div className="chq-ffd-color-filter">
        <TextProperty
          onChange={handleDynamicElementChange}
          activeElement={activeElement}
          defaultValue={dynamicSelection || "static_element"}
          name="dynamic_element"
          options={getDynamicOptions()}
          preview={preview}
          propertyName="dynamic_element"
          useDefaultValue={useDefaultValue}
        >
          <Trans>Dynamic Element</Trans>
        </TextProperty>
      </div>
      {useRelativeTo && (
        <div className="chq-ffd-filter-value">
          <TextProperty
            onChange={handleRelativeToChange}
            activeElement={activeElement}
            defaultValue={relativeSelection || "none"}
            name="relative_to"
            options={relativeToOptions}
            preview={preview}
            propertyName="relative_to"
            useDefaultValue={useDefaultValue}
          >
            <Trans>Relative to</Trans>
          </TextProperty>
        </div>
      )}
    </div>
  );
};

export default DynamicElement;
